<app-auth-page-layout>
  <div class="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar">
    <div class="w-full max-w-md mx-auto mb-5 sm:pt-10">
      <a
        routerLink="/signin"
        class="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20" fill="none" class="size-5">
          <path d="M12.7083 5L7.5 10.2083L12.7083 15.4167" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>{{ 'common.back' | t }}</a>
    </div>

    <div class="flex flex-col justify-center flex-1 w-full max-w-md mx-auto pb-10">
      @switch (phase()) {
        <!-- ── Vérification du statut ─────────────────────────────────────── -->
        @case ('checking') {
          <div class="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
            <svg class="mr-2 size-5 animate-spin text-brand-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle>
              <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path>
            </svg>{{ 'apply.checking' | t }}</div>
        }

        <!-- ── Erreur de vérification ─────────────────────────────────────── -->
        @case ('error') {
          <div class="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
            {{ checkError() }}
          </div>
          <button
            type="button"
            (click)="retry()"
            class="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >{{ 'common.retry' | t }}</button>
        }

        <!-- ── Redirection ────────────────────────────────────────────────── -->
        @case ('done') {
          <div class="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-500/10 dark:text-brand-200">{{ 'apply.activeRedirect' | t }}</div>
        }

        <!-- ── Candidature en attente d'approbation ───────────────────────── -->
        @case ('pending') {
          <div class="text-center">
            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-orange-400">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <h1 class="mt-4 font-semibold text-gray-800 text-title-sm dark:text-white/90">{{ 'apply.pendingTitle' | t }}</h1>
            <p class="mx-auto mt-2 max-w-sm text-sm text-gray-500 dark:text-gray-400">{{ 'apply.pendingBody' | t }}</p>

            @if (existing(); as e) {
              <div class="mt-5 rounded-xl border border-gray-200 p-4 text-left dark:border-gray-700">
                <dl class="space-y-2 text-sm">
                  <div class="flex justify-between gap-3">
                    <dt class="text-gray-500 dark:text-gray-400">{{ 'apply.business' | t }}</dt>
                    <dd class="font-medium text-gray-800 dark:text-white/90">{{ e.businessName || '—' }}</dd>
                  </div>
                  <div class="flex justify-between gap-3">
                    <dt class="text-gray-500 dark:text-gray-400">{{ 'apply.zone' | t }}</dt>
                    <dd class="font-medium text-gray-800 dark:text-white/90">{{ e.zoneCode || '—' }}</dd>
                  </div>
                  <div class="flex justify-between gap-3">
                    <dt class="text-gray-500 dark:text-gray-400">{{ 'common.status' | t }}</dt>
                    <dd class="font-medium text-warning-600 dark:text-orange-400">
                      {{ statusMeta[e.operatorStatus].label | t }}
                    </dd>
                  </div>
                </dl>
              </div>
            }

            <button
              type="button"
              (click)="refreshAndExit()"
              class="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-brand-500 px-4 py-3 text-sm font-medium text-white transition hover:bg-brand-600"
            >{{ 'apply.refresh' | t }}</button>
            <button
              type="button"
              (click)="logout()"
              class="mt-2 inline-flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-gray-500 transition hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >{{ 'common.logout' | t }}</button>
          </div>
        }

        <!-- ── Formulaire de candidature ──────────────────────────────────── -->
        @default {
          <div class="mb-6">
            <h1 class="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">{{ 'apply.title' | t }}</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ 'apply.intro' | t }}</p>
          </div>

          <form [formGroup]="form" (ngSubmit)="submit()" class="space-y-5">
            @if (error()) {
              <div class="rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
                {{ error() }}
              </div>
            }

            <!-- Nom de l'activité -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'apply.businessName' | t }}<span class="text-error-500">*</span>
              </label>
              <input
                type="text"
                formControlName="businessName"
                [placeholder]="'apply.ph.business' | t"
                class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              />
              @if (form.controls.businessName.touched && form.controls.businessName.invalid) {
                <p class="mt-1 text-xs text-error-500">{{ 'apply.businessNameRequired' | t }}</p>
              }
            </div>

            <!-- Zone -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'apply.operatingZone' | t }}<span class="text-error-500">*</span>
              </label>
              <app-pretty-select
                [formControl]="form.controls.zoneCode"
                [options]="zoneOptions()"
                [placeholder]="'apply.ph.zone' | t"
                searchable
              />
              @if (zonesError()) {
                <p class="mt-1 text-xs text-error-500">{{ zonesError() }}</p>
              } @else if (form.controls.zoneCode.touched && form.controls.zoneCode.invalid) {
                <p class="mt-1 text-xs text-error-500">{{ 'apply.zoneRequired' | t }}</p>
              }
            </div>

            <!-- Niveau de licence souhaité (optionnel) -->
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'apply.desiredTier' | t }}</label>
              <app-pretty-select
                [formControl]="form.controls.licenseTier"
                [options]="licenseTierOptions"
                [placeholder]="'apply.ph.tier' | t"
                clearable
              />
            </div>

            <!-- Contact -->
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'apply.phone' | t }}</label>
                <input
                  type="tel"
                  formControlName="phone"
                  [placeholder]="'apply.ph.phone' | t"
                  class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                />
              </div>
              <div>
                <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'apply.email' | t }}</label>
                <input
                  type="email"
                  formControlName="email"
                  [placeholder]="'apply.ph.email' | t"
                  class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                />
              </div>
            </div>

            <button
              type="submit"
              [disabled]="phase() === 'submitting'"
              class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-3 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              @if (phase() === 'submitting') {
                <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle>
                  <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path>
                </svg>
                Envoi…
              } @else {
                Déposer ma candidature
              }
            </button>

            <p class="text-center text-xs text-gray-500 dark:text-gray-400">{{ 'apply.charter' | t }}</p>
          </form>
        }
      }
    </div>
  </div>
</app-auth-page-layout>
